import { GenericEntity } from "@cohesive/api-entities";
import { EntityCardSkeleton } from "@cohesive/ui-components";
import { AlertCircle, GitPullRequest } from "lucide-react";
import React, { useMemo } from "react";
import { EntityDragRow } from "../../../dnd/EntityDragRow";
import { SelectorEmpty } from "../../Selector";
import { CompactListItem } from "../components/CompactListItem";

type ItemType = "issues" | "prs";

interface RepoItemsViewProps {
  items: any[];
  itemType: ItemType;
  isLoading: boolean;
  onItemSelect: (item: any) => void;
  genericById: Map<string, GenericEntity>;
}

export const RepoItemsView: React.FC<RepoItemsViewProps> = ({
  items,
  itemType,
  isLoading,
  onItemSelect,
  genericById,
}) => {
  const itemIds = useMemo(
    () => items.map((i: any) => String(i.data.id)),
    [items],
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <EntityCardSkeleton
            key={i}
            className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
          />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <SelectorEmpty
        message={`No ${itemType === "issues" ? "issues" : "pull requests"} found`}
      />
    );
  }

  return (
    <div>
      {items.map((item: any) => {
        const isIssue = itemType === "issues";
        const icon = isIssue ? (
          <AlertCircle className="h-4 w-4" />
        ) : (
          <GitPullRequest className="h-4 w-4" />
        );

        const number = item.data.number || item.data.content_number;
        const itemTitle =
          item.data.title || item.data.content_title || "Untitled";
        const title = number ? `#${number}: ${itemTitle}` : itemTitle;

        let subtitle = item.data.state || item.data.content_state;
        if (!isIssue && item.data.head_ref && item.data.base_ref) {
          subtitle = `${item.data.head_ref} → ${item.data.base_ref} • ${item.data.state}`;
        }

        const webLink =
          item.data.html_url || item.data.web_link || item.data.content_url;

        const generic = genericById.get(String(item.data.id));

        return (
          <EntityDragRow
            key={item.data.id}
            entity={item}
            generic={generic}
            entityId={String(item.data.id)}
            orderedIds={itemIds}
            onSelect={() => onItemSelect(generic ?? item)}
            dragScope="github"
          >
            <CompactListItem
              icon={icon}
              title={title}
              subtitle={subtitle}
              webLink={webLink}
              onClick={() => onItemSelect(generic ?? item)}
              onAddToContext={() => onItemSelect(generic ?? item)}
            />
          </EntityDragRow>
        );
      })}
    </div>
  );
};
